// Stable per-installation identity. Persists a slug in localStorage
// (key `helm:user-id`) and reflects it on every HTTP fetch as
// `x-helm-user-id`, on every node.register payload, and on every
// outbound envelope's `from.userId`. Display name lives alongside
// under `helm:display-name` and rides the same channels under
// `from.displayName` / register.displayName, but is purely
// informational — the server does no auth on it.
//
// The point of the userId is reconnect-survival: when a tab refreshes
// or reconnects after network blip, the new connection picks up the
// same identity from localStorage. Chat / markers / audit rows
// then attribute back to the same human across the seam, instead
// of getting a fresh nodeId per connect with no thread holding the
// history together.
//
// The slug shape is `<adjective>-<noun>-<4-char-nano>`. The
// adjective + noun give server-side log readers something
// pronounceable on first glance; the nano keeps collisions
// negligible across the population of one lab.

const USER_ID_KEY = 'helm:user-id';
const DISPLAY_NAME_KEY = 'helm:display-name';
const PIN_KEY = 'helm:pin';

const ADJ = [
  'amber',
  'azure',
  'brisk',
  'calm',
  'crisp',
  'dapper',
  'eager',
  'fleet',
  'gentle',
  'happy',
  'jolly',
  'keen',
  'lively',
  'mellow',
  'nimble',
  'plucky',
  'quick',
  'rapid',
  'sharp',
  'silver',
  'sturdy',
  'sunny',
  'swift',
  'vivid',
];
const NOUN = [
  'otter',
  'falcon',
  'fox',
  'lynx',
  'magpie',
  'marten',
  'mink',
  'orca',
  'panda',
  'puma',
  'raven',
  'robin',
  'seal',
  'sparrow',
  'stoat',
  'swan',
  'tern',
  'tiger',
  'wolf',
  'wren',
];
const NANO_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789';

function randomFrom<T>(xs: readonly T[]): T {
  const i = Math.floor(Math.random() * xs.length);
  return xs[i] ?? xs[0]!;
}

function nano(n: number): string {
  let out = '';
  for (let i = 0; i < n; i++) {
    out += NANO_ALPHABET[Math.floor(Math.random() * NANO_ALPHABET.length)];
  }
  return out;
}

/**
 * Generate a fresh user-id slug. Exposed so callers (e.g. the
 * server, when responding to a register with no userId) can mint
 * one using the same shape the client would have.
 */
export function mintUserId(): string {
  return `${randomFrom(ADJ)}-${randomFrom(NOUN)}-${nano(4)}`;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function readLocalStorage(): StorageLike | null {
  if (typeof globalThis === 'undefined') return null;
  try {
    const g = globalThis as { localStorage?: StorageLike };
    if (g.localStorage && typeof g.localStorage.getItem === 'function')
      return g.localStorage;
  } catch {
    // Some browsers throw on access in sandboxed contexts.
  }
  return null;
}

/**
 * Read the persisted user-id, minting (and persisting) a fresh one
 * on first call. Pure — no side effects beyond the localStorage
 * write. Returns the mint result even when localStorage is denied
 * so the in-memory identity stays stable for the lifetime of the
 * tab. Cached in module scope after the first read so repeated
 * lookups don't pay the JSON / storage round-trip.
 */
let cachedUserId: string | null = null;

export function helmUserId(): string {
  if (cachedUserId) return cachedUserId;
  const storage = readLocalStorage();
  if (storage) {
    try {
      const stored = storage.getItem(USER_ID_KEY);
      if (stored && typeof stored === 'string' && stored.length > 0) {
        cachedUserId = stored;
        return stored;
      }
    } catch {
      // fall through to mint
    }
  }
  const minted = mintUserId();
  cachedUserId = minted;
  if (storage) {
    try {
      storage.setItem(USER_ID_KEY, minted);
    } catch {
      // ignore quota / permission errors; the cached value still
      // keeps this tab's identity stable.
    }
  }
  return minted;
}

/**
 * Replace the cached + persisted user-id. Useful when the server's
 * `node.register.response` returns a userId different from what
 * the client offered (e.g. legacy fallback path, conflict
 * resolution) — the client adopts the server's choice so future
 * connects line up.
 */
export function setHelmUserId(next: string): void {
  if (!next || typeof next !== 'string') return;
  cachedUserId = next;
  const storage = readLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(USER_ID_KEY, next);
  } catch {
    // ignore
  }
}

/**
 * Operator-entered name. Persisted; surfaced on `from.displayName`
 * and on the register payload's `displayName`. Empty string ⇒ no
 * name set; callers should fall back to userId for display.
 */
let cachedDisplayName: string | null = null;

export function helmDisplayName(): string {
  if (cachedDisplayName !== null) return cachedDisplayName;
  const storage = readLocalStorage();
  if (storage) {
    try {
      const stored = storage.getItem(DISPLAY_NAME_KEY);
      if (typeof stored === 'string') {
        cachedDisplayName = stored;
        return stored;
      }
    } catch {
      // fall through
    }
  }
  cachedDisplayName = '';
  return '';
}

export function setHelmDisplayName(next: string): void {
  cachedDisplayName = next ?? '';
  const storage = readLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(DISPLAY_NAME_KEY, cachedDisplayName);
  } catch {
    // ignore
  }
}

/**
 * Operator-entered 4-digit PIN. Persisted in localStorage so a
 * reload doesn't re-prompt. The PIN is intentionally non-sensitive
 * — its only job is to disambiguate two researchers with the same
 * first name in audit rows ("Alice (1234)" vs "Alice (5678)"). The
 * UI should be explicit about that so people don't reuse a bank
 * PIN. Empty string = unset.
 */
let cachedPin: string | null = null;

export function helmPin(): string {
  if (cachedPin !== null) return cachedPin;
  const storage = readLocalStorage();
  if (storage) {
    try {
      const stored = storage.getItem(PIN_KEY);
      if (typeof stored === 'string') {
        cachedPin = stored;
        return stored;
      }
    } catch {
      // fall through
    }
  }
  cachedPin = '';
  return '';
}

export function setHelmPin(next: string): void {
  cachedPin = (next ?? '').trim();
  const storage = readLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(PIN_KEY, cachedPin);
  } catch {
    // ignore
  }
}

/** True when the operator has entered both a display name and a 4-digit PIN. */
export function helmIdentityComplete(): boolean {
  return helmDisplayName().trim().length > 0 && /^\d{4}$/.test(helmPin());
}

/**
 * Wipe the persisted display name + PIN so the next page load
 * presents a fresh login prompt. The auto-minted user-id is left
 * in place so anything keyed off it (chat history, snapshots) stays
 * coherent across the logout boundary — the user is still the same
 * installation, just stepping into a different operator chair.
 */
export function clearHelmIdentity(): void {
  cachedDisplayName = '';
  cachedPin = '';
  const storage = readLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(DISPLAY_NAME_KEY);
    storage.removeItem(PIN_KEY);
  } catch {
    // ignore
  }
}

/**
 * Test seam: wipe the cached values so a vitest run can simulate a
 * fresh tab. Production code never calls this.
 */
export function __resetIdentityForTests(): void {
  cachedUserId = null;
  cachedDisplayName = null;
  cachedPin = null;
}
